// 不動産（売買物件）詳細 src/pages/RealEstateDetail.jsx

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import { formatPrice, formatArea, formatUnit, extractMapSrc } from "../utils/format";

const RealEstateDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    fetch(`/api/realestate?id=${encodeURIComponent(id)}`)
      .then((res) => {
        if (!res.ok) {
          setNotFound(true);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setItem(data);
          setActiveIdx(0);
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <p className="text-center py-8">この物件は現在公開されていません。</p>
    );
  if (!item) return <p className="text-center py-8">読み込み中...</p>;

  // 物件スペック（値があるものだけ表示）
  const specs = [
    { label: "販売価格", value: formatPrice(item.price, item.taxIncluded !== false) },
    { label: "表面利回り", value: formatUnit(item.surfaceYield, "%") },
    { label: "民泊稼働率", value: item.minpakuOccupancy },
    { label: "物件種別", value: item.propertyType },
    { label: "所在地", value: item.address },
    { label: "地番", value: item.chiban },
    { label: "住居表示", value: item.residenceDisplay },
    { label: "交通", value: item.transport },
    { label: "土地面積", value: formatArea(item.landArea) },
    { label: "建物面積", value: formatArea(item.buildingArea) },
    { label: "間取り", value: item.layout },
    { label: "戸数", value: item.units },
    { label: "構造", value: item.structure },
    { label: "設備", value: item.equipment },
    { label: "築年", value: item.buildYear },
    { label: "地目/権利", value: item.landCategory },
    { label: "用途地域", value: item.zoning },
    { label: "建ぺい率", value: formatUnit(item.buildingCoverage, "%") },
    { label: "容積率", value: formatUnit(item.floorAreaRatio, "%") },
    { label: "道路幅員", value: formatUnit(item.roadWidth, "m") },
    { label: "接道状況", value: item.roadAccess },
    { label: "防火規制", value: item.fireZone },
    { label: "高度地区", value: item.heightDistrict },
    { label: "都市計画道路", value: item.cityPlanRoad },
    { label: "他の法令上の制限", value: item.otherRestrictions },
    { label: "取引形態", value: item.transactionType },
    { label: "引渡日", value: item.handoverDate },
  ].filter((s) => s.value);

  return (
    <>
      {/* パンくずリスト */}
      <Breadcrumb extra={item.name} />

      <div className="max-w-3xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold mb-4">{item.name}</h1>

        {/* 写真ギャラリー（メイン＋サムネイル） */}
        {Array.isArray(item.photos) && item.photos.length > 0 && (
          <div className="mb-6">
            <img
              src={item.photos[activeIdx] || item.photos[0]}
              alt={`${item.name}のメイン写真`}
              className="w-full h-72 md:h-96 object-contain bg-gray-100 rounded"
            />
            {item.photos.length > 1 && (
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                {item.photos.map((url, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className={`shrink-0 rounded border-2 ${
                      i === activeIdx ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img
                      src={url}
                      alt={`${item.name}-${i + 1}`}
                      className="h-16 w-24 object-cover rounded"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 物件スペック */}
        {specs.length > 0 && (
          <table className="w-full text-sm mb-6 border-t">
            <tbody>
              {specs.map((s) => (
                <tr key={s.label} className="border-b">
                  <th className="text-left align-top py-2 pr-4 w-28 text-gray-600 font-medium">
                    {s.label}
                  </th>
                  <td className="py-2">
                    {s.label === "販売価格" ? (
                      <span className="text-lg font-bold text-red-600">
                        {s.value}
                      </span>
                    ) : (
                      s.value
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 物件説明 */}
        {item.body && (
          <div className="mb-6 whitespace-pre-wrap">{item.body}</div>
        )}

        {/* 詳細資料（PDF） */}
        {Array.isArray(item.detailFiles) && item.detailFiles.length > 0 && (
          <div className="mb-6">
            <h2 className="font-semibold mb-2">詳細資料</h2>
            <ul className="list-disc pl-5">
              {item.detailFiles.map((f, i) => (
                <li key={i}>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {f.name || `資料${i + 1}.pdf`}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* お問い合わせ */}
        <div className="mb-6">
          <a
            href={item.inquiryUrl || "/contact"}
            target={item.inquiryUrl ? "_blank" : undefined}
            rel={item.inquiryUrl ? "noopener noreferrer" : undefined}
            className="inline-block bg-primary text-white px-6 py-3 rounded hover:opacity-90"
          >
            この物件について問い合わせる
          </a>
        </div>

        {/* Google Map 埋め込み */}
        {item.mapUrl && (
          <div className="mb-4">
            <h2 className="font-semibold mb-2">地図</h2>
            <iframe
              title={`${item.name}の地図`}
              src={extractMapSrc(item.mapUrl)}
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </div>
        )}

        <Link
          to="/businessContent/real-estate"
          className="text-blue-600 hover:underline"
        >
          ← 一覧に戻る
        </Link>
      </div>
    </>
  );
};

export default RealEstateDetail;
